/**
 * Configuración de almacenamiento local para modo demo
 * Todos los datos se guardan en localStorage, sin necesidad de backend
 */

// Claves de localStorage
const STORAGE_KEYS = {
  BRANCHES: "bizantino_gelato_branches",
  PRODUCTS: "bizantino_gelato_products",
  RECIPES: "bizantino_gelato_recipes",
  RECIPE_INGREDIENTS: "bizantino_gelato_recipe_ingredients",
  ACTIVE_BRANCH: "bizantino_gelato_active_branch",
};

export { STORAGE_KEYS };

/**
 * Utilidades para localStorage
 */
const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error leyendo ${key} de localStorage:`, error);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);

      // Verificar que realmente se guardó correctamente
      const retrieved = localStorage.getItem(key);
      if (retrieved !== serialized) {
        throw new Error(
          `No se pudo verificar el guardado de ${key} en localStorage`
        );
      }

      return true;
    } catch (error) {
      console.error(`Error guardando ${key} en localStorage:`, error);
      // Lanzar el error para que las funciones que llaman puedan manejarlo
      throw new Error(
        `Error al guardar ${key} en localStorage: ${error.message}`
      );
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error eliminando ${key} de localStorage:`, error);
      return false;
    }
  },
};

/**
 * Simular delay de red para hacer el demo más realista
 */
const delay = (ms = 300) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Simular respuestas de API con estructura similar
 */
const createResponse = (data) => ({
  data,
  status: 200,
  statusText: "OK",
});

/**
 * GET request - Simulado con localStorage
 */
export const GET = async (endpoint) => {
  await delay(200);
  // Por ahora solo simulamos, la lógica real estará en los endpoints
  return createResponse({});
};

/**
 * POST request - Simulado con localStorage
 */
export const POST = async (endpoint, data) => {
  await delay(300);
  return createResponse(data);
};

/**
 * PUT request - Simulado con localStorage
 */
export const PUT = async (endpoint, data) => {
  await delay(300);
  return createResponse(data);
};

/**
 * PATCH request - Simulado con localStorage
 */
export const PATCH = async (endpoint, data) => {
  await delay(300);
  return createResponse(data);
};

/**
 * DELETE request - Simulado con localStorage
 */
export const DELETE = async (endpoint) => {
  await delay(200);
  return createResponse({});
};

/**
 * POST con FormData - Simulado con localStorage
 */
export const POST_FORM = async (endpoint, data) => {
  await delay(300);
  // Convertir FormData a objeto simple
  const obj = {};
  if (data instanceof FormData) {
    for (const [key, value] of data.entries()) {
      obj[key] = value;
    }
  } else {
    Object.assign(obj, data);
  }
  return createResponse(obj);
};

/**
 * PATCH con FormData - Simulado con localStorage
 */
export const PATCH_FORM = async (endpoint, data) => {
  await delay(300);
  // Convertir FormData a objeto simple
  const obj = {};
  if (data instanceof FormData) {
    for (const [key, value] of data.entries()) {
      obj[key] = value;
    }
  } else {
    Object.assign(obj, data);
  }
  return createResponse(obj);
};

/**
 * Verificar integridad de datos en localStorage
 * Útil para debugging y validación en modo demo
 */
const verifyStorageIntegrity = () => {
  const results = {
    valid: true,
    errors: [],
    warnings: [],
    stats: {},
  };

  try {
    // Verificar que todas las claves existan y sean arrays válidos
    Object.entries(STORAGE_KEYS).forEach(([key, storageKey]) => {
      try {
        const data = storage.get(storageKey, null);

        if (key !== "ACTIVE_BRANCH") {
          // Para arrays, verificar que sea un array válido
          if (data !== null && !Array.isArray(data)) {
            results.valid = false;
            results.errors.push(`${key}: Los datos no son un array válido`);
          } else if (Array.isArray(data)) {
            results.stats[key] = {
              count: data.length,
              valid: true,
            };
          } else {
            results.warnings.push(`${key}: No hay datos inicializados`);
          }
        } else {
          // Para ACTIVE_BRANCH, verificar que sea un string válido
          if (data !== null && typeof data !== "string") {
            results.valid = false;
            results.errors.push(`${key}: El valor no es un string válido`);
          } else {
            results.stats[key] = {
              value: data,
              valid: true,
            };
          }
        }
      } catch (error) {
        results.valid = false;
        results.errors.push(`${key}: Error al leer - ${error.message}`);
      }
    });

    // Verificar relaciones de datos
    try {
      const recipes = storage.get(STORAGE_KEYS.RECIPES, []);
      const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
      const products = storage.get(STORAGE_KEYS.PRODUCTS, []);
      const branches = storage.get(STORAGE_KEYS.BRANCHES, []);

      // Verificar que los ingredientes de recetas apunten a recetas válidas
      const recipeIds = new Set(recipes.map((r) => r.id));
      const orphanIngredients = ingredients.filter(
        (ing) => !recipeIds.has(ing.recipe)
      );
      if (orphanIngredients.length > 0) {
        results.warnings.push(
          `Se encontraron ${orphanIngredients.length} ingredientes huérfanos (sin receta asociada)`
        );
      }

      // Verificar que los productos apunten a sucursales válidas
      const branchIds = new Set(branches.map((b) => b.id));
      const orphanProducts = products.filter(
        (p) => p.branch && !branchIds.has(p.branch)
      );
      if (orphanProducts.length > 0) {
        results.warnings.push(
          `Se encontraron ${orphanProducts.length} productos con sucursales inválidas`
        );
      }

      // Verificar que las recetas apunten a sucursales válidas
      const orphanRecipes = recipes.filter(
        (r) => r.branch && !branchIds.has(r.branch)
      );
      if (orphanRecipes.length > 0) {
        results.warnings.push(
          `Se encontraron ${orphanRecipes.length} recetas con sucursales inválidas`
        );
      }
    } catch (error) {
      results.warnings.push(`Error verificando relaciones: ${error.message}`);
    }

    return results;
  } catch (error) {
    results.valid = false;
    results.errors.push(`Error crítico en verificación: ${error.message}`);
    return results;
  }
};

// Exportar utilidades de storage y delay
export { storage, delay, verifyStorageIntegrity };
